<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="false"
                title="Cell Counter Result"
                @onClickCloseButton="onClickToClose"
            />

          <div v-if="!isAllowSubmit" class="d-flex justify-content-end">
            <button :disabled="loading" type="button" @click="goToMachinePrint" class="btn btn-primary me-1 waves-effect waves-float justify-content-end waves-light">
              <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              Print
            </button>
          </div>

        </div>

      <div class="my-2 px-2 mb-1">
       <div class="row">
         <div class="col-12 col-md-6">
           <label class="form-label title">Bill Number <span class="required">*</span></label>
           <input
               class="form-control"
               tabindex="1"
               type="text"
               placeholder="Enter Your Bill Number"
               v-model="billNumber"
               @keyup.enter="getInvestigationDataBillNumber"
           >
         </div>
         <div class="col-12 col-md-6 mb-2">
           <label class="form-label title">Sample ID</label>
           <input
               class="form-control"
               tabindex="1"
               type="text"
               readonly
               v-model="sampleId"
           >
         </div>
       </div>

        <TableHeaderData
            :leftSideData="tableHeaderData.leftSideData"
            :rightSideData="tableHeaderData.rightSideData"
            :rightColumnWidth="rightColumnWidth"
            v-model:dischargeDate="dischargeDate"
        >
          <template #left-table-slot>
            <tr>
              <td class="table-column" :style="`width: ${rightColumnWidth}`">Report Prepared By</td>
              <td class="table-divider"></td>
              <td class="table-row dropdown-consultant">
                <AsyncSelect
                    placeholder="Select Consultant"
                    v-model="preparedBy"
                    :api-service="fetchServiceList"
                    :autoSelectFirst="true"
                    :additional-query="{ resource_type: 'human_resources', user_role: 'pathologist' }"
                    :format-label="option => option.name"
                />
              </td>
            </tr>
          </template>

          <template #right-table-slot>
            <tr>
              <td class="table-column" :style="`width: ${rightColumnWidth}`">Report Approved By</td>
              <td class="table-divider"></td>
              <td class="table-row  dropdown-consultant">
                <AsyncSelect
                    placeholder="Select Consultant"
                    v-model="approvedBy"
                    :autoSelectFirst="true"
                    :api-service="fetchServiceList"
                    :additional-query="{ resource_type: 'human_resources', user_role: 'pathologist_doctor' }"
                    :format-label="option => option.name"
                />
              </td>
            </tr>

            <tr>
              <td class="table-column" :style="`width: ${rightColumnWidth}`">Delivery Date</td>
              <td class="table-divider"></td>
              <td class="table-row  dropdown-consultant">
                <div class="col-md-7 col-lg-12">
                  <input
                      class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                      tabindex="1"
                      type="date"
                      v-model="deliveryDate"
                  >
                </div>
              </td>
            </tr>

          </template>
        </TableHeaderData>
      </div>

        <InvestigationReport 
            :formattedGenerals="reports.formattedGenerals"
            :antibioticsDrowdown="antibioticsDrowdown"
            v-if="reports && reports.formattedGenerals"
            @selection-change="selectedGenerals = $event"
        />

      <div class="mb-3 px-2" v-if="false">
        <label for="attachment" class="form-label fw-bold">Attachment</label>
        <BlobFileViewer :attachments="attachments" />
        <FileUpload
            :is-drag-active="true"
            button-text="Upload Documents"
            v-model="attachments"
        >
        </FileUpload>
      </div>

        <div class="row px-2" v-if="isAllowSubmit">
            <div class="col-12 col-sm-12 col-md-12 col-lg-12 mb-1 text-center">
                <button :disabled="loading" type="button" @click="submit()" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                    <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    Save
                </button>

              <button :disabled="loadingPdf" type="button" @click="submit(true)" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                <div v-if="loadingPdf" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Save & Print(With Header)
              </button>
              <button :disabled="loadingPdf" type="button" @click="submit(true, true)" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                <div v-if="loadingPdf" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Save & Print(Without Header)
              </button>
              <button v-if="route.query.is_pathologist === 'true'
                                && route.query.status !== 'approved'"
                      :disabled="loadingStatus" type="button"
                      @click="updateStatus" class="btn btn-primary me-1 waves-effect waves-float waves-light">
                <div v-if="loadingStatus" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Approve
              </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import InvestigationReport from '@/components/molecule/company/hospital/investigation-general/InvestigationReport.vue';
import handleHospital from '@/services/modules/hospital'
import handleCompany from "@/services/modules/company";
import {inject, onMounted, reactive, ref, watch} from 'vue';
import {useStore} from 'vuex';
import {useRoute, useRouter} from 'vue-router';
import { getCurrentDateTime, dateOfBirthConvertToAge, formatDateToDdMmYy, groupBy, formatDate, convertToTitleCase } from '@/services/utils/global';
import investigationResultPdfPrinter from '@/services/utils/investigationResultPdfPrinterOld';
import investigationMachineResultPdf from '@/services/utils/investigationMachineResultPdf';
import handleBarcode from "@/services/modules/barcode";
import handleInventory from '@/services/modules/inventory';
import TableHeaderData from '@/components/molecule/company/hospital/investigation-general/InvestigationGeneralHeader.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import FileUpload from "@/components/atom/FileUpload";
import BlobFileViewer from "@/components/atom/BlobFileViewer";
import useDate from "@/services/utils/day";

const { fetchCellCounterMachine, updateInvestigationGeneral, saveInvestigationAttachment, updateInvestigationStatus, fetchServiceList, fetchInvestigationReportByBillNumber } = handleHospital();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const { exportToPDF } = investigationResultPdfPrinter();
const { exportToPDF: exportToMachinePdf } = investigationMachineResultPdf();
const { fetchBarcode } = handleBarcode();
const { fetchHome } = handleInventory();

const store = useStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const loader = ref(false);
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const reports = ref(null);
const preparedBy = ref(null);
const approvedBy = ref(null);
const attachments = ref([]);
const companyInfo = ref({});
const barcode = ref('');
const loadingPdf = ref(false);
const loadingStatus = ref(false);
const qrcode = ref('');
const barcodePatient = ref('');
const billNumber = ref('');
const sampleId = ref('');
const selectedGenerals = ref({});
const customizationPdfFields = ref([]);
const tableHeaderData = reactive({
  leftSideData: [],
  rightSideData: [],
});
const antibioticsDrowdown = ref([]);
const rightColumnWidth = '30%';
const dischargeDate = ref(getCurrentDateTime());
const saleId = ref('')
const investigationsIds = ref('')
const reportType  = ref('laboratory')
const isAllowSubmit = ref(false)
const date = useDate();
const deliveryDate = ref('');

const submit = async (print=false, isHideHeaderFooter = false) => {
    const formattedData = formatFormData();

    if (!route.query.machine_data_id || route.query.machine_data_id == undefined || route.query.machine_data_id == ''){
      showError("Machine Data Id Not Exist")
      return
    }

    if (! saleId.value){
      showError("Sale Data Not Found")
      return
    }

  print ? loadingPdf.value = true : loading.value = true;
  await updateInvestigationGeneral(formattedData).then( async (res) => {
        loading.value = false
        if (!res.status) return showError(res.message);
        onClickToClose();
        await saveAttachment();
        if(print) {
          goToPrint(isHideHeaderFooter);
          loadingPdf.value = false;
        }
        return showSuccess(res.message);
    }).catch(err => {
        loading.value = false
    }).finally(() => {
        loading.value = false
        loadingPdf.value = false
    })
}

const goToPrint = async (isHideHeaderFooter) => {
  try {
    await Promise.all([
      getInvestigationDataBillNumber(),
      getCompanyInfo(),
    ]);

    generatePdf(companyInfo.value, {
      ...reports.value,
      formattedGenerals: getPrintableGenerals(),
    }, isHideHeaderFooter);
  } catch (err) {
    console.log(err);
  }
};

const goToMachinePrint = async () => {
  try {
    await Promise.all([
      fetchInitialData(),
      getCompanyInfo(),
    ]);

    generateMachinePdf(companyInfo.value, {
      ...reports.value,
      formattedGenerals: getPrintableGenerals(),
    });
  } catch (err) {
    console.log(err);
  }
};

const getPrintableGenerals = () => {
  return Object.keys(selectedGenerals.value).length > 0
      ? selectedGenerals.value
      : reports.value.formattedGenerals;
};

const generatePdf = async (company, data, isHideHeaderFooter) => {
  const user = store.state.user.user;
  const isHideFooter = isHideHeaderFooter;
  const isHideHeader = isHideHeaderFooter;
  const isDownload = false;
  const firstProductKey = Object.keys(data.formattedGenerals)[0];
  const firstReportCategory = Object.keys(data.formattedGenerals[firstProductKey])[0];
  const firstInvestigation = data.formattedGenerals[firstProductKey][firstReportCategory][0];
  const reportName = firstInvestigation.head_name;

  let qrcodeData = JSON.stringify({
    ref_no: data.id,
    ref_date: data.date,
    created_by: user.name,
    created_date_time: formatDate(new Date())
  })
  const barCodeQuery = `?code=${data.bill_number}&qrcode=${qrcodeData}&height=20&width=1`;
  const barCodePatientQuery = `?code=${data.contact_profile.serial_no}&height=20`;
  await Promise.all([
    fetchBarcode(barCodeQuery).then((res) => {
      barcode.value = res.barcode;
      qrcode.value = res.qrcode;
    }),
    fetchBarcode(barCodePatientQuery).then((res) => {
      barcodePatient.value = res.barcode;
    })
  ]).then(() => {
    exportToPDF(
        company,
        data,
        barcode.value,
        qrcode.value,
        barcodePatient.value,
        user.name,
        convertToTitleCase(reportName),
        isHideHeader,
        isHideFooter,
        isDownload,
        customizationPdfFields.value
    )
  }).catch((e) => {
    console.log('Something went wrong');
  })
}

const generateMachinePdf = async (company, data) => {
  const user = store.state.user.user;
  const isHideFooter = false;
  const isHideHeader = false;
  const isDownload = false;

  let qrcodeData = JSON.stringify({
    ref_no: data.machineData.id,
    created_by: user.name,
    created_date_time: formatDate(new Date())
  })
  const barCodeQuery = `?code=${data.machineData.id}&qrcode=${qrcodeData}&height=20&width=1`;
  await Promise.all([
    fetchBarcode(barCodeQuery).then((res) => {
      qrcode.value = res.qrcode;
    }),
  ]).then(() => {
    exportToMachinePdf(
        company,
        data,
        qrcode.value,
        user.name,
        isHideHeader,
        isHideFooter,
        isDownload,
        customizationPdfFields.value
    )
  }).catch((e) => {
    console.log('Something went wrong');
  })
}

const getCompanyInfo = async () => {
  const companyId = route.params.companyId;
  await fetchCompanyInfo(companyId)
      .then((res) => {
        if(res.status){
          companyInfo.value = res.data;
        }
      }).catch((error) => {
        console.log(error);
      });
}

const saveAttachment = async () => {
  const formData = new FormData();
  let previousFileIds = [];

  formData.append('company_id', route.params.companyId);
  formData.append('report_type', reportType.value)
  formData.append('sale_master_id', saleId.value);

  for(const key in attachments.value){
    if(typeof attachments.value[key] === 'object' && attachments.value[key].hasOwnProperty('id')) {
      previousFileIds.push(attachments.value[key].id);
      continue;
    }
    formData.append('attachments[]', attachments.value[key]);
  }

  formData.append('previous_file_ids', JSON.stringify(previousFileIds))

  await saveInvestigationAttachment(formData, getQuery());
}

const updateStatus = async () => {
  loadingStatus.value = true;
  if(!confirm('Are you sure you want to update status?')) return;

  const payload = {
    investigation_ids: investigationsIds.value,
    status: 'approved',
    company_id: route.params.companyId,
  };

  const res = await updateInvestigationStatus(payload);

  if(! res.status) {
    loadingStatus.value = false;
    return showError(res.message);
  }

  showSuccess(res.message);
  loadingStatus.value = false;
  onClickToClose();
}

const getAntibioticDropdown = async () => {
  antibioticsDrowdown.value = [];
  try {
    const res = await fetchHome();
    if(res.status) {
      Object.keys(res.data.antibiotics_dropdown).forEach((val)=>{
        antibioticsDrowdown.value.push({
          id: val,
          name: res.data.antibiotics_dropdown[val]
        });
      });
    }
  } catch (err) {
    console.log(err);
  }
}

const formatFormData = () => {
    const data = {
        'investigation_results': [],
        'company_id': route.params.companyId,
        'sale_id': saleId.value,
        'machine_data_id': route.query.machine_data_id,
        'prepared_by': preparedBy.value ? preparedBy.value.id : '',
        'approved_by': approvedBy.value ? approvedBy.value.id : '',
        'delivery_date': deliveryDate.value ? deliveryDate.value : '',
    }

    Object.values(reports.value.formattedGenerals).forEach(sections => {
      Object.values(sections).forEach(arr => {
        data.investigation_results.push(...arr)
      })
    })

    data.investigation_results = groupBy(
        data.investigation_results,
        'investigation_general_id'
    )

    return data;
}

const onClickToClose = () => {
    router.push({
        name: 'cell-counter-machine',
        params: route.params,
        query: route.query
    })
}

const getQuery = () => {
  return `?company_id=${route.params.companyId}&machine_type=cell_counter_machine&investigation_type=${reportType.value}`;
}

const fetchInitialData = async () => {
    const machineDataId = route.query.machine_data_id;
    const query = getQuery();

    loader.value = true;

    await fetchCellCounterMachine(query, machineDataId)
        .then((res) => {
            if (!res.status) return
            reports.value = res.data;
            if (res.data.machineData && res.data.machineData.bill_number){
              billNumber.value = res.data.machineData.bill_number;
              getInvestigationDataBillNumber()
            }
            if (res.data.machineData && res.data.machineData.sample_id){
              sampleId.value = res.data.machineData.sample_id;
            }
        }).catch((err) => {
            console.log(err)
        });

    loader.value = false
}

const getInvestigationDataBillNumber = async () => {
  const machineDataId = route.query.machine_data_id;
  let query = getQuery();
  query += '&machine_data_id='+ machineDataId;
  query += '&bill_number='+ billNumber.value;
  loader.value = true;
  reports.value = []
  isAllowSubmit.value = false
  saleId.value = '';
  investigationsIds.value = ''

  await fetchInvestigationReportByBillNumber(query)
      .then((res) => {
        if (!res.status){
          showError(res.message)
          return
        }
        reports.value = res.data;
        saleId.value = res.data.id
        investigationsIds.value = res.investigations_ids
        isAllowSubmit.value = true
        setInvestigationData();
      }).catch((err) => {
        console.log(err)
      });

  loader.value = false
}

const getAge = (birthday) => {
  if(!birthday) return '';
  const {year} = dateOfBirthConvertToAge(birthday);
  return year;
}

const setInvestigationData = () => {
  if (reports.value.approved_by) {
    approvedBy.value = reports.value.approved_by_resource;
  }
  if (reports.value.prepared_by) {
    preparedBy.value = reports.value.prepared_by_resource;
  }
  deliveryDate.value = reports.value.expected_delivery_date ? reports.value.expected_delivery_date : date.currentDate();
  formatLeftSideData(reports.value.contact_profile, reports.value)
  setRightSideData(reports.value);
  attachments.value = reports.value.investigation_attachments;
}

const formatLeftSideData = (patientInfo, reports) => {
  const age = getAge(patientInfo.birthday);

  tableHeaderData.leftSideData = [
    { key: 'Patient ID', value: patientInfo.serial_no },
    { key: 'Patient Name', value: patientInfo.full_name },
    { key: 'Sex & Age', value: patientInfo.gender + ' | ' +   (age ? (age + ' years') : '')},
    { key: 'Patient Phone No', value: patientInfo.mobile_no },
    { key: 'Ref By', value: reports.service_resource.name },
  ];
}

const setRightSideData = (reports) => {
  tableHeaderData.rightSideData = [
    { key: 'Invoice no', value: reports.bill_number },
    { key: 'Invoice date', value: formatDateToDdMmYy(reports.date) },
    { key: 'Investigation SL No', value: ''}
  ]
}

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${route.params.companyId}&customization_type=pdf&type=investigation_report_pdf`;
  try {
    const res = await fetchActiveCustomizationField(companyQuery);
    customizationPdfFields.value = res.data;
  } catch (error) {
    console.error("Error fetching customization fields:", error);
  }
}

onMounted( async () => {
  deliveryDate.value = date.currentDate();
  if (route.query.machine_data_id && route.query.machine_data_id != undefined && route.query.machine_data_id != ''){
    await fetchInitialData();
  }
  await getAntibioticDropdown();
  await getActiveFiledCustomization();
})
</script>

<style scoped>
address{
    margin-bottom: 0 !important;
}
.title{
   font-size: 14px;
   font-weight: bold;
}
</style>